import React, { useEffect } from "react";
import CatalogTableC from "../../components/Catalogs/CatalogTableC";
import { IContent } from "../../interfaces/models/IContent";
import {
  contentStatusEnum,
  isApprovedEnum,
  submissionTypeEnum,
} from "../../Enums/ContentEnums";
import { UseFirmList } from "../../hooks/Catalogs/useFirm";
import { UsePracticeIndustryList } from "../../hooks/Catalogs/usePracticeIndustry";
import { UseRegionList } from "../../hooks/Catalogs/useRegionListOptions";
import PaginatedTable from "../../components/Organismos/Table/PaginatedTable";
import { useDispatch } from "react-redux";
import { contentsSlice } from "../../redux";
import ShapeEdit from "../../components/Atomos/Icons/ShapeEdit";
import {
  formatDateToString,
  formatTimeSpanToString,
} from "../../utils/functions";
import useDataLoader2 from '../../hooks/useDataLoader2';
import { defaultPageSize } from "../../utils/const";
import Filters, { IFilterData } from "../../components/Catalogs/Filters/Filters";
import PersistentLink from "../../components/Moleculas/PersistentLink";
import { exportAllDataLoader2 } from "../../utils/exportAllDataLoader2";

const Content: React.FC = () => {
  const dispatch = useDispatch();

  const endpoint = '/content';
  const { 
    data, 
    totalCount, 
    fetchData, 
    setFilter, 
    sort, 
    setSort, 
    pagination, 
    setPagination,
    inputFilters,
    setInputFilters,
    filter,
  } = useDataLoader2<any>({
    endpoint,
    defaultPageSize,
    defaultSorting: { field: "title", order: "asc" },
  });

  const columnsDef = React.useMemo(() => [
    {
      id: "count",
      header: "#",
      enableSorting: false,
      cell: ({ rowIndex }: { rowIndex: number; row: any; value: any }) => (
        <p> {rowIndex + 1 + pagination.pageIndex * pagination.pageSize} </p>
      ),
    },
    { id: "shortId", header: "Short ID" },
    { id: "title", header: "Title" },
    { id: "firms", header: "Firms" },
    { id: "type", header: "Type" },
    {
      id: "isApproved",
      header: "Status",
      cell: ({ value }: { rowIndex: number; row: any; value: any }) => {
        let text = "";
        let color = "";

        switch (value) {
          case "Yes":
            text = "Approved";
            color = "#2AA042";
            break;
          case "No":
            text = "Rejected";
            color = "#FF6109";
            break;
          case "Pending":
            text = "Pending";
            color = "#C6A80D";
            break;
          default:
            text = value;
            break;
        }

        return <span style={{ color }}>{text}</span>;
      },
    },
    {
      id: "writtenDate",
      header: "Written Date",
    },
    {
      id: "publishedDate",
      header: "Published Date",
    },
    {
      id: "reviewedDate",
      header: "Reviewed Date",
    },
    {
      id: "practiceIndustries",
      header: "Practice Industries",
    },
    { id: "views", header: "Views" },
    { id: "jurisdictions", header: "Jurisdictions" },
    {
      id: "regions",
      header: "Regions",
    },
    {
      id: "createdDate",
      header: "Created Date"
    },
    {
      id: "edit",
      header: "Edit",
      enableSorting: false,
      cell: ({ row, rowIndex }: { rowIndex: number; row: any; value: any }) => (
        <PersistentLink
          className="edit"
          to={`Edit/${row.id}`}
          state={{content: data[rowIndex]}}
          persistingData={{
            pagination,
            sort,
            filter, 
            inputFilters, 
          }}
        >
          Edit
          <div className="editsvg">
            <ShapeEdit />
          </div>
        </PersistentLink>
      ),
    },
  ], [pagination.pageIndex, pagination.pageSize, sort, filter, inputFilters, data]);

  const { firmData } = UseFirmList();

  useEffect(() => {
    dispatch(contentsSlice.actions.InitializeContent());
  }, []);


  const formatData = (data: IContent[]) => {
    return data.map((d: IContent) => ({
      ...d,
      type: submissionTypeEnum[d.submissionType],
      isApproved: isApprovedEnum[d.isApproved],
      writtenDate: d.writtenDate && formatDateToString(new Date(d.writtenDate)),
      publishedDate:
        d.publishedDate === "pending"
          ? contentStatusEnum.pending
          : d.publishedDate && formatDateToString(new Date(d.publishedDate)),
      reviewedDate:
        d.reviewedDate === "pending"
          ? contentStatusEnum.pending
          : d.reviewedDate && formatDateToString(new Date(d.reviewedDate)),
      firms: d.firmNames,
      practiceIndustries: d.practiceIndustryNames,
      jurisdictions: d.jurisdictionNames,
      regions: d.regionNames,
      views: d.views ?? 0,
      createdDate: d.createdDate && formatTimeSpanToString(d.createdDate),
    }));
  };

  const { practiceIndustryData } = UsePracticeIndustryList();
  const { regionData } = UseRegionList();

  const statusOptions = Object.entries(isApprovedEnum).map(([id, name]) => ({
    id,
    name,
  }));

  const filtersData: IFilterData[] = React.useMemo(() => [
    {
      name: "Title",
      type: "text",
      comparison: "CONTAINS",
    },
    {
      name: "Firm",
      fieldName: "memberFirms",
      objectData: firmData?.map((f: any) => ({id:f.id, name: f.name})) || [],
    },
    {
      name: "Status",
      fieldName: "isApproved",
      objectData: statusOptions,
      disabledWrite: true,
      customTitle: "Is Approved:",
    },
    {
      name: "Practice Industry",
      fieldName: "practiceIndustries",
      objectData: practiceIndustryData || [],
    },
    {
      name: "Region",
      fieldName: "regions",
      objectData: regionData || [],
    },
    {
      name: "Jurisdiction",
      fieldName: "jurisdictions",
      objectData: regionData || [],
    },
    {
      name: "Written Date",
      fieldName: "writtenDate",
      type: "date",
      comparison: "BETWEEN",
    },
    {
      name: "Published Date",
      fieldName: "publishedDate",
      type: "date",
      comparison: "BETWEEN",
    },
  ], [firmData, practiceIndustryData, regionData]);

  return (
    <>
        <CatalogTableC
          title={`Content Catalog`}
          catalogName={"CONTENTS"}
          route={"Content"}
          catalogShortName={"CONTENT"}
          totalCount={totalCount}
          newItemAction={() => {
            dispatch(contentsSlice.actions.InitializeContent());
          }}
        >
          <Filters 
            filtersData={filtersData} 
            filterValues={inputFilters}
            setFilterValues={setInputFilters} 
            Search={() => fetchData()}
            clearSelections={() => fetchData(true)}
            setValueForQuery={setFilter}
          />
          <PaginatedTable
            data={data || []}
            columnsDef={columnsDef}
            formatData={formatData}
            sorting={sort}
            onSortingChange={setSort}
            totalCount={totalCount || 0}
            pagination={pagination}
            onPaginationChange={setPagination}
            endpoint={endpoint}
            onExportAll={() =>
              exportAllDataLoader2<any>({
                endpoint,
                filter,
                sort,
                pageSize: 1000,
              })
            }
          />
        </CatalogTableC>
    </>
  );
};

export default Content;
